import { useEffect, useRef, useState } from 'react';
import type {
  AccountType,
  FirstHour,
  LinkUse,
  Notice,
  Pattern,
  ShadowbanInput,
  Tool,
} from '@/lib/shadowbanDiagnostic';
import { SOURCE_LABEL, diagnoseShadowban } from '@/lib/shadowbanDiagnostic';

interface Option {
  value: string;
  label: string;
  muted?: boolean;
}

type StepKey = 'notice' | 'tools' | 'pattern' | 'account' | 'links' | 'firstHour';

interface Step {
  key: StepKey;
  question: string;
  hint?: string;
  multi?: boolean;
  options: Option[];
}

const STEPS: Record<StepKey, Step> = {
  notice: {
    key: 'notice',
    question: 'LinkedIn vous a-t-il prévenu de quelque chose ?',
    hint: 'Un message à la connexion, un email, un avis sous un post.',
    options: [
      { value: 'restriction', label: 'Oui, mon compte est restreint (message à la connexion ou par email)' },
      { value: 'visibility', label: 'Oui, un post a été limité, étiqueté ou retiré' },
      { value: 'none', label: 'Non, rien du tout' },
      { value: 'unknown', label: 'Je ne sais pas', muted: true },
    ],
  },
  tools: {
    key: 'tools',
    question: 'Utilisez-vous l’un de ces outils ou pratiques ?',
    hint: 'Plusieurs réponses possibles. Un outil de programmation officiel de LinkedIn ne compte pas.',
    multi: true,
    options: [
      { value: 'extension', label: 'Une extension qui envoie des invitations, des messages ou extrait des profils' },
      { value: 'agent', label: 'Un agent IA qui clique sur LinkedIn à ma place (Claude in Chrome, par exemple)' },
      { value: 'pod', label: 'Un groupe où l’on s’engage à réagir aux posts des autres (pod)' },
      { value: 'bought', label: 'De l’engagement acheté (likes, commentaires)' },
    ],
  },
  pattern: {
    key: 'pattern',
    question: 'Comment la baisse se présente-t-elle ?',
    options: [
      { value: 'sudden', label: 'Chute brutale : mes posts faisaient bien mieux avant' },
      { value: 'always', label: 'Portée basse depuis toujours ou presque' },
      { value: 'one_post', label: 'Un seul post a mal marché' },
      { value: 'unknown', label: 'Je ne sais pas', muted: true },
    ],
  },
  account: {
    key: 'account',
    question: 'Publiez-vous depuis un profil ou une page entreprise ?',
    options: [
      { value: 'personal', label: 'Un profil personnel' },
      { value: 'company', label: 'Une page entreprise' },
    ],
  },
  links: {
    key: 'links',
    question: 'Vos posts contiennent-ils un lien dans le corps du texte ?',
    options: [
      { value: 'most', label: 'Presque tous' },
      { value: 'some', label: 'Certains' },
      { value: 'none', label: 'Aucun (ou le lien est en commentaire)' },
      { value: 'unknown', label: 'Je ne sais pas', muted: true },
    ],
  },
  firstHour: {
    key: 'firstHour',
    question: 'Combien de réactions recevez-vous en général dans la première heure ?',
    options: [
      { value: 'zero', label: 'Aucune' },
      { value: 'few', label: '1 ou 2' },
      { value: 'several', label: '3 ou plus' },
      { value: 'unknown', label: 'Je ne sais pas', muted: true },
    ],
  },
};

/**
 * Le parcours dépend des réponses : un compte restreint n'a pas besoin de
 * parler de liens, ni de première heure. Il raccourcit donc à deux écrans.
 */
function stepsFor(notice: string | undefined): StepKey[] {
  if (notice === 'restriction') return ['notice', 'tools'];
  return ['notice', 'tools', 'pattern', 'account', 'links', 'firstHour'];
}

type Answers = Partial<Record<StepKey, string | string[]>>;

export function ShadowbanWizard() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [selected, setSelected] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    headingRef.current?.focus();
  }, [step]);

  const keys = stepsFor(answers.notice as string | undefined);
  const isDone = step >= keys.length;

  const goNext = (key: StepKey, value: string | string[]) => {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    setSelected([]);
    setStep((current) => current + 1);
  };

  const goBack = () => {
    const previous = keys[step - 1];
    const stored = answers[previous];
    setSelected(Array.isArray(stored) ? stored : []);
    setStep((current) => current - 1);
  };

  const restart = () => {
    setAnswers({});
    setSelected([]);
    setCopied(false);
    setStep(0);
  };

  if (isDone) {
    const input: ShadowbanInput = {
      notice: (answers.notice as Notice) ?? 'unknown',
      pattern: (answers.pattern as Pattern) ?? 'unknown',
      tools: ((answers.tools as Tool[]) ?? []).filter(Boolean),
      account: (answers.account as AccountType) ?? 'personal',
      links: (answers.links as LinkUse) ?? 'unknown',
      firstHour: (answers.firstHour as FirstHour) ?? 'unknown',
    };
    const result = diagnoseShadowban(input);

    const copyResult = () => {
      const text = [
        result.headline,
        ...result.causes.map((c) => `- ${c.title} (${SOURCE_LABEL[c.source]}) : ${c.why}`),
        'À faire :',
        ...result.actions.map((a, i) => `${i + 1}. ${a}`),
        result.caveat,
      ].join('\n\n');
      try {
        navigator.clipboard
          .writeText(text)
          .then(() => setCopied(true))
          .catch(() => {
            // Écriture refusée par le navigateur : le bouton reste inerte.
          });
      } catch {
        // Pas de Clipboard API : on n'interrompt rien.
      }
    };

    return (
      <div className="nz-wizard">
        <div className="nz-wizard__result">
          <h2 className="nz-wizard__result-headline" tabIndex={-1} ref={headingRef}>
            {result.headline}
          </h2>

          {result.causes.length > 0 ? (
            <ol className="nz-shadowban__causes">
              {result.causes.map((cause) => (
                <li key={cause.id} className="nz-shadowban__cause">
                  <div className="nz-shadowban__cause-title">{cause.title}</div>
                  <div className="nz-shadowban__source">{SOURCE_LABEL[cause.source]}</div>
                  <p>{cause.why}</p>
                </li>
              ))}
            </ol>
          ) : null}

          <div className="nz-shadowban__todo">À faire, dans cet ordre</div>
          <ol className="nz-shadowban__actions">
            {result.actions.map((action) => (
              <li key={action}>{action}</li>
            ))}
          </ol>

          <div className="nz-wizard__tip">{result.caveat}</div>
          <div className="nz-wizard__actions">
            <button type="button" className="nz-btn-secondary" onClick={copyResult}>
              {copied ? 'Copié' : 'Copier le résultat'}
            </button>
            <button type="button" className="nz-wizard__back" onClick={restart}>
              Recommencer
            </button>
          </div>
        </div>
      </div>
    );
  }

  const current = STEPS[keys[step]];
  const progress = Math.round((step / keys.length) * 100);

  const toggle = (value: string) =>
    setSelected((list) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]));

  return (
    <div className="nz-wizard">
      <div
        className="nz-wizard__progress"
        role="progressbar"
        aria-valuenow={progress}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Progression du test"
      >
        <div className="nz-wizard__bar" style={{ width: `${progress}%` }} />
      </div>

      <div className="nz-wizard__step-label">
        Question {step + 1} sur {keys.length}
      </div>
      <h2 className="nz-wizard__question" tabIndex={-1} ref={headingRef}>
        {current.question}
      </h2>
      {current.hint ? <p className="nz-shadowban__hint">{current.hint}</p> : null}

      <div className="nz-wizard__options">
        {current.options.map((option) => {
          if (current.multi) {
            const on = selected.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={on}
                className={`nz-wizard__option${on ? ' nz-wizard__option--on' : ''}`}
                onClick={() => toggle(option.value)}
              >
                {option.label}
              </button>
            );
          }
          return (
            <button
              key={option.value}
              type="button"
              className={`nz-wizard__option${option.muted ? ' nz-wizard__option--muted' : ''}`}
              onClick={() => goNext(current.key, option.value)}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {current.multi ? (
        <div className="nz-wizard__actions">
          <button type="button" className="nz-btn-primary" onClick={() => goNext(current.key, selected)}>
            {selected.length === 0 ? 'Aucun de ces outils' : 'Continuer'}
          </button>
        </div>
      ) : null}

      {step > 0 ? (
        <button type="button" className="nz-wizard__back" onClick={goBack}>
          ← Retour
        </button>
      ) : null}
    </div>
  );
}
